import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';

import { keepPreviousForCompany } from '@/lib/company-placeholder';

import { useApi } from '@/lib/api-context';

import {
  competenciesResponseSchema,
  overviewResponseSchema,
  readersResponseSchema,
  type DocumentReaders,
  type ReaderCompetencies,
  type TrainingOverview,
} from './types';

// Consultas de la pantalla Capacitaciones (so=360). La empresa activa entra al
// queryKey (nunca se sirve caché de otra empresa) y cada respuesta se valida con Zod.

export const PAGE_SIZE = 25;

interface Scope {
  companyId: string | null;
  enabled: boolean;
}

/** Indicadores y tabla paginada; mantiene la página previa mientras carga la nueva. */
export function useTrainingOverview(
  page: number,
  { companyId, enabled }: Scope,
): UseQueryResult<TrainingOverview> {
  const api = useApi();
  return useQuery({
    queryKey: ['training-overview', companyId, page],
    queryFn: async () => {
      const offset = (page - 1) * PAGE_SIZE;
      const raw = await api.get<unknown>(
        `/api/v1/quality/training/overview?limit=${PAGE_SIZE}&offset=${offset}`,
      );
      return overviewResponseSchema.parse(raw).data;
    },
    enabled,
    placeholderData: keepPreviousForCompany<TrainingOverview>(companyId),
  });
}

/** Lectores de la versión vigente de un documento. */
export function useDocumentReaders(
  entryId: string | null,
  { companyId, enabled }: Scope,
): UseQueryResult<DocumentReaders> {
  const api = useApi();
  return useQuery({
    queryKey: ['training-readers', companyId, entryId],
    queryFn: async () => {
      const raw = await api.get<unknown>(
        `/api/v1/quality/training/documents/${entryId ?? ''}/readers`,
      );
      return readersResponseSchema.parse(raw).data;
    },
    enabled: enabled && entryId !== null,
    // Reintentos: la política global (query-client) no repite un 404 (documento inexistente
    // o de otra empresa).
  });
}

/** Competencias de una persona que lee el documento (se piden al desplegarla). */
export function useReaderCompetencies(
  entryId: string,
  lmsUserId: number | null,
  { companyId, enabled }: Scope,
): UseQueryResult<ReaderCompetencies> {
  const api = useApi();
  return useQuery({
    queryKey: ['training-competencies', companyId, entryId, lmsUserId],
    queryFn: async () => {
      const raw = await api.get<unknown>(
        `/api/v1/quality/training/documents/${entryId}/readers/${String(lmsUserId)}/competencies`,
      );
      return competenciesResponseSchema.parse(raw).data;
    },
    enabled: enabled && lmsUserId !== null,
  });
}

/** Reprograma el último envío fallido de la capacitación de un documento. Al terminar
 *  (bien o con conflicto) se refresca la tabla para mostrar el estado real. */
export function useRetryDispatch(entryId: string): UseMutationResult<void, Error, void> {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      await api.post<unknown>(`/api/v1/quality/training/documents/${entryId}/dispatch/retry`, {});
    },
    onSettled: () => void qc.invalidateQueries({ queryKey: ['training-overview'] }),
  });
}
